import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { default as api, type ShopItem } from '../service/api';
import { createPersistStorage } from './utils/persist';

interface ShopItemsState {
  items: ShopItem[];
  isLoading: boolean;
  error: string | null;
}

interface ShopItemsActions {
  fetchShopItems: () => Promise<void>;
  clearShopItems: () => void;
  getShopItemById: (itemId: string) => ShopItem | undefined;
}

type ShopItemsStore = ShopItemsState & ShopItemsActions;

type ShopItemsStorePersist = Pick<ShopItemsStore, 'items'>;

const useShopItemsStore = create<ShopItemsStore>()(
  persist<ShopItemsStore, [], [], ShopItemsStorePersist>(
    (set, get) => ({
      items: [],
      isLoading: false,
      error: null,

      fetchShopItems: async () => {
        const hasCachedItems = get().items.length > 0;
        set({ isLoading: !hasCachedItems, error: null });

        try {
          const items = await api.shopItemsGet();

          set({
            items,
            isLoading: false,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : 'Failed to fetch shop items',
            isLoading: false,
          });
        }
      },

      getShopItemById: (itemId: string) => {
        return get().items.find(item => item._id === itemId);
      },

      clearShopItems: () =>
        set({
          items: [],
          error: null,
        }),
    }),
    {
      name: 'shop-items-store',
      storage: createPersistStorage<ShopItemsStorePersist>(),
      partialize: state => ({
        items: state.items,
      }),
    },
  ),
);

export default useShopItemsStore;
export type { ShopItem };
